import React from 'react';
import autobind from 'autobind-decorator';
import {Col, Row} from 'react-bootstrap';
import 'chartjs-plugin-datalabels';
import {defaults, Line} from 'react-chartjs-2';

import {BaseComponent} from "../../BaseComponent";
import {Conditional} from "../../Conditional";
import {ConfirmationDialog} from "../../Common/ConfirmationDialog";
import {HelpIcon} from "../../Common/HelpIcon";
import {StreamBankLink} from "../../Common/StreamBankLink";

import {
    DocumentUtils,
    HistoryUtils,
    ObjectUtils,
} from "../../../Utils";

import {service as ConservationActionPhaseService} from '../../../Services/ConservationActionPhaseService';
import {service as EnvironmentalBenefitService} from '../../../Services/EnvironmentalBenefitService';
import {service as ProgramBenefitStatisticService} from '../../../Services/ProgramBenefitStatisticService';
import {service as ProgramService} from '../../../Services/ProgramService';
import {service as UnitService} from '../../../Services/UnitService';

import {StreamBankColor} from "../../../StreamBankColor";


import {EnvironmentalBenefitFilter} from '../../Common/EnvironmentalBenefitFilter';
import {ConservationActionPhaseFilter} from '../../Common/ConservationActionPhaseFilter';
import {StreamBankChartLegend} from "./StreamBankChartLegend";
import {NoModeledUpliftWarning} from "../../Common/NoModeledUpliftWarning";
import {PermissionCheckerEnhance} from "../../Common/Enhancements";

// Disable animating charts by default.
defaults.global.defaultFontFamily = 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif';
defaults.global.defaultFontSize = '16';

let colorIndex = 0;
let usedColors = {};

@PermissionCheckerEnhance
export class TrackingComponent extends BaseComponent {
    constructor() {
        super();

        this.setInitialState({
            program: null,
            environmentalBenefits: [],
            conservationActionPhases: [],
            units: [],
            currentEnvironmentalBenefitId: null,
            currentConservationActionPhaseIndex: null,
            currentConservationActionPhaseId: null,
            currentConfirmationStatusId: null,
            data: null,
            series: [
                {
                    key: "Projects",
                    label: "Projects",
                    enabled: true,
                }
            ],
            cachedProgramData: {},
            popup: null,
        });

        // custom positioner for the tooltip
        Chart.Tooltip.positioners.cursor = function(chartElements, coordinates) {
            const chartHeight = chartElements[0]._chart.height;
            const tooltipEl = document.getElementById('chartjs-tooltip');
            const paddingFromBottomToXAxis = 100;

            if (!tooltipEl) {
                return coordinates;
            } else if (coordinates.y + tooltipEl.offsetHeight < chartHeight - paddingFromBottomToXAxis) {
                return {
                    x: coordinates.x + 10,
                    y: coordinates.y,
                };
            } else {
                return {
                    x: coordinates.x + 10,
                    y: coordinates.y - tooltipEl.offsetHeight,
                }
            }
        };

        this.chartReference = React.createRef();
    }

    @autobind
    getProgramId(props) {
        if(!props) {
            props = this.props;
        }

        return props.match.params.cProgId || props.match.params.pProgId
    }

    @autobind
    shouldReloadData(prevProps, prevState) {
        return this.getProgramId() !== this.getProgramId(prevProps);
    }

    @autobind
    loadData() {
        const { HasPermission } = this.props;
        
        super.loadData();

        this.props.MakePageRequest(
            () => Promise.all([
                ProgramService.GetProgram(this.getProgramId()),
                EnvironmentalBenefitService.Get(),
                ConservationActionPhaseService.Get(),
                UnitService.Get(),
            ]),
            ([program, environmentalBenefits, conservationActionPhases, allUnits]) => {
                let envBenefits = environmentalBenefits.result;
                let units = allUnits.result;
                let prog = program.result;
                let filteredEnvBenefits = envBenefits.filter(
                    (x) => prog.ProgramEnvironmentalBenefits?.some(
                        (y) => y.environmentalBenefitId === x.environmentalBenefitId
                    )
                );
                let defaultEvnBenefitId = filteredEnvBenefits[0] !== undefined ? filteredEnvBenefits[0].environmentalBenefitId : 1;

                let augmentedConservationActionPhases = [];
                
                if(HasPermission('view_unconfirmed_modeled_uplift')) {
                    augmentedConservationActionPhases = conservationActionPhases.result
                        .filter((phase) => ['eligibility', 'design', 'as-built'].includes(phase.code));
                }

                augmentedConservationActionPhases.push({
                    code: "confirmed",
                    conservationActionPhaseId: 3,
                    confirmationStatusId: 3,
                    name: "Confirmed"
                });

                let conservationActionPhaseIndex = DocumentUtils.GetQueryStringParameter("conservationActionPhase");
                let selectedConservationActionPhase = augmentedConservationActionPhases[conservationActionPhaseIndex];
                if (selectedConservationActionPhase == null) {
                    conservationActionPhaseIndex = augmentedConservationActionPhases.length-1;
                    selectedConservationActionPhase = augmentedConservationActionPhases[conservationActionPhaseIndex];
                }
                let defaultPhaseId = selectedConservationActionPhase?.conservationActionPhaseId;
                let defaultStatusId = selectedConservationActionPhase?.confirmationStatusId;

                this.setState({
                    program: prog,
                    environmentalBenefits: filteredEnvBenefits,
                    units,
                    conservationActionPhases: augmentedConservationActionPhases,
                    currentEnvironmentalBenefitId: DocumentUtils.GetQueryStringParameter("environmentalBenefitId") || defaultEvnBenefitId,
                    currentConservationActionPhaseIndex: conservationActionPhaseIndex,
                    currentConservationActionPhaseId: defaultPhaseId,
                    currentConfirmationStatusId: defaultStatusId,
                    data: null
                }, () => {
                    this.fetchStats();
                });
            },
            (x) => {
                if(!x.xhr) {
                    throw x;
                }
                this.props.DefaultErrorHandler(x);
            }
        );
    }

    @autobind
    getColor(key, colors) {
        if(key.startsWith("Milestone") || key === "Projects") {
            // we don't record these colors as "used"
            // so just skip through
            return new StreamBankColor();
        } else {
            let name = key;
            if(name.startsWith("Implemented ")) {
                name = name.substr("Implemented ".length);
            } else if(name.startsWith("Projected ")) {
                name = name.substr("Projected ".length);
            } else if(name.startsWith("Target ")) {
                name = name.substr("Target ".length);
            }

            if(!usedColors[name]) {
                usedColors[name] = colors[colorIndex++];
            }
            return usedColors[name];
        }
    }

    @autobind
    getColors(count) {
        let ret = StreamBankColor.GetThemeColors();

        if(ret.length === 0) { //if the theme is empty. lets just use a dark gray to seed things.
            ret.push(new StreamBankColor({hex: "#DDDDDD"}));
        }

        //if we're below the requested color count, lets just reuse colors and lighten them up.
        let i = 0;
        while (ret.length < count) {
            let color = ret[i++];

            ret.push(color.darken(30));
        }

        return ret;
    }

    @autobind
    getData(programData, series) {
        let me = this;

        if(!programData) {
            if (this.state.cachedProgramData) {
                programData = this.state.cachedProgramData;
            }
        }

        if(!programData || !programData.dataSets) {
            return null;
        }

        let data = {
            labels: programData.labels,
            datasets: []
        };

        // lets generate a few distinct colors for each dataSet.
        // minus two because milestones and program target are hard coded colors
        // (and projects series doesn't even use this array)
        let colors = this.getColors(Object.entries(programData.dataSets).length);

        // reserve the first color in the theme for "total"
        let totalColor = this.getColor('Total', colors);

        //foreach data set.
        for (const [key, value] of Object.entries(programData.dataSets)) {
            let color = this.getColor(key, colors);

            // apply legend selections
            let foundSeries = series.find((x) => x.key === key);
            if(foundSeries && !foundSeries.enabled) {
                continue;
            }

            // for each xAxis entry lets pull out the value for this dataset
            const set = data.labels.map(x =>
                // set null if we don't have a value so the xAxis and the data match up
                value.data[x] !== undefined ? value.data[x] : null
            );

            const isImplementedTotal = (value.parent == '' && key.startsWith("Implemented "));

            let dataset = {
                label: key,
                fill: isImplementedTotal,
                lineTension: 0.1,
                borderColor: color.toHex(),
                backgroundColor: color.withAlpha(10).toHex(),
                borderCapStyle: 'butt',
                borderDash: [0, 0],
                borderDashOffset: 0.0,
                borderJoinStyle: 'miter',
                borderWidth: isImplementedTotal ? 3 : 1.5,
                pointBorderColor: color.toHex(),
                pointBackgroundColor: color.toHex(),
                pointBorderWidth: 0,
                pointHoverRadius: 0,
                pointHoverBackgroundColor: color.toHex(),
                pointHoverBorderColor: color.toHex(),
                pointHoverBorderWidth: 0,
                pointRadius: 0,
                pointHitRadius: 10,
                pointStyle: 'line',
                spanGaps: true,
                data: set
            };

            // override the style based on the data type.
            // and increment color if one was used
            if(key.startsWith("Projected")) {
                dataset.borderDash = [3, 3];
            } else if(key.startsWith("Milestone")) {
                let pointColor = "#FF6E9E";

                dataset.pointStyle = 'rectRot';
                dataset.showLine = false;
                dataset.pointRadius = 10;
                dataset.pointHoverRadius = 10;
                dataset.pointHitRadius = 10;

                dataset.borderColor = pointColor;
                dataset.backgroundColor = pointColor;
                dataset.pointBorderColor = pointColor;
                dataset.pointBackgroundColor = pointColor;
                dataset.pointHoverBackgroundColor = pointColor;
                dataset.pointHoverBorderColor = pointColor;
            } else if(key.startsWith("Target ")) {
                dataset = Object.assign({}, dataset, {
                    borderColor: "#ccc",
                    backgroundColor: "#ccc",
                    borderDash: [3, 3],
                    pointBorderColor: "#ccc",
                    pointBackgroundColor: '#fff',
                    pointBorderWidth: 1,
                    pointHoverRadius: 1,
                    pointHoverBackgroundColor: "#ccc",
                    pointHoverBorderColor: "#ccc",
                    pointHoverBorderWidth: 1,
                    pointRadius: 1,
                    pointStyle: 'line',
                    pointHitRadius: 10
                });
            } else { // if(key.startsWith("Implemented "))
                // Implemented or other unexpected series name
            }

            data.datasets.push(dataset);
        }

        //add the project count line.
        if(programData.implementedProjects) {

            let foundSeries = series.find((x) => x.key === "Projects");
            if(foundSeries && foundSeries.enabled) {

                const set = data.labels.map(x =>
                    // set null if we don't have a value so the xAxis and the data match up
                    programData.implementedProjects[x] !== undefined ? Object.keys(programData.implementedProjects[x]).length : null
                );

                data.datasets.push({
                    label: "Projects",
                    yAxisID: "project_yaxis",
                    fill: false,
                    lineTension: 0.1,
                    backgroundColor: "#e6e6e6",
                    borderColor: "#e6e6e6",
                    borderCapStyle: 'butt',
                    borderDash: [],
                    borderDashOffset: 0.0,
                    borderJoinStyle: 'miter',
                    pointBorderColor: "#e6e6e6",
                    pointBackgroundColor: "#e6e6e6",
                    pointBorderWidth: 1,
                    pointHoverBackgroundColor: "#e6e6e6",
                    pointHoverBorderColor: "#e6e6e6",
                    pointHoverBorderWidth: 1,
                    borderWidth: 1,

                    pointStyle: 'circle',
                    showLine: false,
                    pointRadius: 5,
                    pointHoverRadius: 15,
                    pointHitRadius: 5,

                    datalabels: {
                        display: (context) => context.active,
                        color: "#010101"
                    },
                    projects: programData.implementedProjects,

                    data: set
                });
            }
        }

        if(data.datasets) {
            data.datasets.sort(this.dataSetDrawSeriesSort);
        }

        return data;
    }

    @autobind
    fetchStats() {
        if(
            this.state.currentEnvironmentalBenefitId &&
            this.state.currentConservationActionPhaseId &&
            this.state.program
        ) {
            let confirmationStatusId = this.state.currentConfirmationStatusId != null ? this.state.currentConfirmationStatusId : null;

            ProgramBenefitStatisticService.GetProgramBenefitStats(
                this.state.program.programId,
                this.state.currentEnvironmentalBenefitId,
                this.state.currentConservationActionPhaseId,
                confirmationStatusId
            ).then((x) => {
                let updatedSeries = this.updateSeriesFromDataSets(x.result.dataSets);
                this.setState({
                    data: this.getData(x.result, updatedSeries),
                    series: updatedSeries,
                    cachedProgramData: x.result
                }, () => {
                    HistoryUtils.PushQueryStringUpdate({
                        environmentalBenefitId: this.state.currentEnvironmentalBenefitId,
                        conservationActionPhase: this.state.currentConservationActionPhaseIndex
                    });
                });
            });
        } else {
            this.setState({data: null});
        }
    }

    @autobind
    updateEnvironmentalBenefit(environmentalBenefitId) {
        this.setState({currentEnvironmentalBenefitId: environmentalBenefitId}, () => {
            this.fetchStats();
        });
    }

    @autobind
    updateConservationActionPhase(index) {

        const { conservationActionPhases } = this.state;

        const conservationActionPhase = conservationActionPhases[index];

        if(!conservationActionPhase) {
            return;
        }

        this.setState({
            currentConservationActionPhaseIndex: index,
            currentConservationActionPhaseId: conservationActionPhase.conservationActionPhaseId,
            currentConfirmationStatusId: conservationActionPhase.confirmationStatusId
        }, () => {
            this.fetchStats();
        });
    }

    @autobind
    renderLegend() {
    }

    @autobind
    toggleSeries(label) {
        let newSeries = ObjectUtils.Clone(this.state.series);

        newSeries = newSeries.map((series) => {
            if(series.label === label) {
                series.enabled = !series.enabled;
            }

            return series;
        });


        this.setState({
            data: this.getData(null, newSeries),
            series: newSeries
        });
    }

    @autobind
    updateSeriesFromDataSets(dataSets) {
        let result = ObjectUtils.Clone(this.state.series);

        Object.keys(dataSets).forEach((key) => {

            let found = this.state.series.find((x) => x.key === key);

            if(!found) {
                // TODO: we could maybe inject colors here too?
                // HACK: splice into position 1 instead of push
                // so that we keep "Projects" at the bototm of the
                // legend
                result.splice(1, 0, {
                    key: key,
                    label: key,
                    parent: dataSets[key].parent,
                    enabled: true
                });
            }
        });

        return result;
    }

    @autobind
    formatNumber(num, decimalPlaces = null, sigPlaces = 3) {

        // if decimalPlaces is null use significant digits
        if(decimalPlaces == null) {
            var parts = num.toFixed(Math.max(decimalPlaces, sigPlaces)).split('.');
            decimalPlaces = decimalPlaces ?? Math.max((sigPlaces - (parts[0]?.length ?? 0)), 0);
        }

        var parts = num.toFixed(decimalPlaces).split('.');
        var integerPart = parts[0];
        var decimalPart = parts[1] ? parts[1] : undefined; // .slice(0, decimalPlaces) : undefined;
        return integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + (decimalPart ? "." + decimalPart : "");
    }

    // method to sort by a specific order to the top
    @autobind
    sortByStringPrefixes(sortOrder) {
        return (a, b) => {
            for (const stringPrefix of sortOrder) {

                const aMatchesPrefix = a.startsWith(stringPrefix);
                const bMatchesPrefix = b.startsWith(stringPrefix);

                if (aMatchesPrefix) {
                    return -1;
                } else if (bMatchesPrefix) {
                    return 1;
                }
            }

            return 0;
        }
    }

    // single method to sort tooltip and legend
    @autobind
    dataSeriesSort(aData, bData) {
        const aWords = aData.label.split(' ');
        const bWords = bData.label.split(' ');

        // The first word is the type of line
        const aType = aWords.shift();
        const bType = bWords.shift();

        // The second is the program or sub-program series name
        const aName = aWords.join(' ');
        const bName = bWords.join(' ');

        // This is the series name given for the top level program
        const programName = 'Total';

        // This is the sort method used to
        // order series with the same program name
        const sortWithinProgram = this.sortByStringPrefixes(
            ['Target', 'Projected', 'Implemented']
        );

        // if the names are the same sort by category
        if(aName === bName && aName !== '') {
            return sortWithinProgram(aType, bType);
        }

        // if this is the parent program move it to the top
        if(aName === programName) {
            return -1;
        } else if(bName === programName) {
            return 1;
        }

        // if this is the Projects or Milestone move it to the bottom
        if(aType === 'Projects') {
            return 1;
        } else if(bType === 'Projects') {
            return -1;
        } else if(aType === 'Milestone') {
            return 1;
        } else if(bType === 'Milestone') {
            return -1;
        }

        // otherwise sort alphabetically by name
        return aName.localeCompare(bName);
    }

    // same as dataSeriesSort except we
    // move certain types to the top
    // so they appear over others
    @autobind
    dataSetDrawSeriesSort(aData, bData) {
        let a = aData.label;
        let b = bData.label;

        // Milestones, targets and projects to the top
        if(a.startsWith("Milestone")) {
            return -1;
        }
        else if(b.startsWith("Milestone")) {
            return 1;
        }
        else if(a.startsWith("Target ")) {
            return -1;
        }
        else if(b.startsWith("Target ")) {
            return 1;
        }
        else if(a.startsWith("Projects")) {
            return -1;
        }
        else if(b.startsWith("Projects")) {
            return 1;
        }
        // Implemented total to the bottom
        else if(a.startsWith("Implemented Total")) {
            return 1;
        }
        else if(b.startsWith("Implemented Total")) {
            return -1;
        }

        this.dataSeriesSort(aData, bData);
    }

    // used for the chart title
    @autobind
    getParentProgramShortTitle() {
        return (this.state.program.shortName ?? '') != '' ? this.state.program.shortName : this.state.program.name;
    }

    // used for the series names
    @autobind
    getParentProgramShortName() {
        return this.state.program.shortName;
    }

    @autobind
    getProgramTargetSeriesName() {
        return "Target Total"; // + this.getParentProgramName();
    }

    @autobind
    readableColor(hex) {
        // from https://codepen.io/ItsASine/pen/GxbaNZ
        const decimalFromColor = parseInt('0x' + hex.substring(1), 16);
        const threshold = 0xffffff / 1.5; // fraction of black
        return (decimalFromColor < threshold) ?
            '#fefefe' :
            '#191919';
    }

    @autobind
    padZero(str, len) {
        len = len || 2;
        let zeros = new Array(len).join('0');
        return (zeros + str).slice(-len);
    }

    @autobind
    getFactorLabelForValue(value) {
        value = Math.abs( parseFloat(value));

        if (value >= 1000000000000) {
            return {
                factor: 1000000000000,
                label: " Trillion"
            };
        }

        if (value >= 1000000000) {
            return {
                factor: 1000000000,
                label: " Billion"
            };
        }

        if (value >= 1000000) {
            return {
                factor: 1000000,
                label: " Million"
            };
        }

        if (value >= 1000) {
            return {
                factor: 1000,
                label: " Thousand"
            };
        }

        return {
            factor: 1,
            label: ""
        };
    }

    @autobind
    formatYAxisNumber(value, decimalPlaces = null) {
        let {factor, label} = this.getFactorLabelForValue(value);
        return this.formatNumber(value / factor, decimalPlaces) + label;
    }

    // this is bound to the chart
    hoverElements(event, elements) {

        let hoveredElement = this.getElementAtEvent(event);
        let isClickable = false;

        if (hoveredElement?.[0]?.$datalabels?.$context?.dataset?.label === 'Projects') {
            isClickable = true;
        }

        event.target.style.cursor = isClickable ? 'pointer' : 'default';
    }

    @autobind
    clickElement(element) {
        const { dataIndex, dataset } = element[0].$datalabels.$context;

        if (dataset.label === 'Projects') {
            // hide tooltip
            this.chartReference?.current?.chartInstance?.canvas.dispatchEvent(new MouseEvent('mouseout', {bubbles: true}));

            const year = this.state.data.labels[dataIndex];
            const projects = dataset.projects[year];

            this.setState({popup: {
                    title: `Projects Implemented In ${year}`,
                    content: Object.keys(projects).map(x => (
                        <div>
                            <StreamBankLink Href={`${projects[x].link}/summary`}>
                                {projects[x].name}
                            </StreamBankLink>
                        </div>
                    )),
                }});
        }
    }

    render() {
        let me = this;

        // don't render page before it is loaded
        if(this.state.program === null) {
            return null;
        }

        // y-axis factor for making
        // big numbers more readable
        let maxYvalue = 0;
        if(this.state.data) {
            let merged = [].concat.apply([], this.state.data.datasets.map((x) => x.data));
            maxYvalue = Math.max.apply(null, merged);
        }
        let {factor, label: factorLabel} = this.getFactorLabelForValue(maxYvalue);

        let envBenefit = this.state.environmentalBenefits.find((x) =>
            x.environmentalBenefitId == this.state.currentEnvironmentalBenefitId
        );
        if(!envBenefit && this.state.environmentalBenefits.length > 0) {
            envBenefit = this.state.environmentalBenefits[0];
        }

        let unit = this.state.units.find((x) => x.unitId === envBenefit?.unitId);

        let yAxisLabelText = envBenefit ? envBenefit.name : 'Uplift';
        if (unit) yAxisLabelText += ` ( ${unit.name} )`;

        if (unit && envBenefit.name === "Solar Load Reduction" &&
            (this.getProgramId() == 'srsp' || this.getProgramId() == 'srsp-demo' ||
                this.getProgramId() == '2030-irrigation-upgrade-program-demo' ) ) {
            yAxisLabelText += " 121-day attenuated value"
        }

        let legendConfig = {
            display: false,
            position: "right",
            fullWidth: true,
            labels: {
                usePointStyle: true
            }
        };

        let chartOptions = {
            layout: {
                padding: {
                    left: 20,
                    right: 0,
                    top: 20,
                    bottom: 20
                }
            },
            tooltips: {
                mode: 'index',
                intersect: false,
                position: 'cursor',
                // hide tooltip for projects dataset
                filter: ((tooltipItem, chartData) => {
                    const label = chartData.datasets[tooltipItem.datasetIndex].label;
                    const excludedSeriesNames = [this.getProgramTargetSeriesName(), 'Projects'];
                    return !excludedSeriesNames.includes(label)
                }),
                fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                // Disable the on-canvas tooltip
                enabled: false,
                // Setup custom tooltip logic. Based off this: https://www.chartjs.org/docs/latest/configuration/tooltip.html#external-custom-tooltips
                custom: function(tooltipModel) { // Don't use arrow syntax. "this" gets bounded weird by chartjs. I don't know why this wouldn't just pass in whatever it wants the context to be. :/
                    // Tooltip Element
                    let tooltipEl = document.getElementById('chartjs-tooltip');

                    // Create element on first render
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chartjs-tooltip';
                        tooltipEl.innerHTML = "<table></table>";
                        document.body.appendChild(tooltipEl);
                    }

                    // Hide if no tooltip
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = 0;
                        return;
                    }

                    // Set caret Position
                    tooltipEl.classList.remove('above', 'below', 'no-transform');
                    tooltipEl.classList.add(tooltipModel.yAlign ? tooltipModel.yAlign : 'no-transform');

                    // Set Text
                    if (tooltipModel.body && tooltipModel.dataPoints[0]) {
                        // index of the data item in the dataset
                        const index = tooltipModel.dataPoints[0].index;

                        let titleLines = tooltipModel.title || [];
                        let innerHtml = '<thead>';
                        titleLines.forEach((title) => {
                            innerHtml += '<tr><th colSpan="2">' + title + '</th></tr>';
                        });
                        innerHtml += '</thead><tbody>';

                        const datasets = [...me.state.data.datasets];

                        datasets
                            .sort(me.dataSeriesSort)
                            .forEach((target) => {
                                const label = target.label;
                                const value = target.data[index];
                                if (value !== null) {
                                    let style = `background: ${target.borderColor};`;
                                    style += `border-color: ${target.borderColor};`;

                                    let displayValue = me.formatYAxisNumber(value)
                                    let valueWithUnit = displayValue;
                                    if (unit && label !== "Projects") {
                                        valueWithUnit = unit.isPrefix ? `${unit.abbreviation} ${displayValue}` : `${displayValue} ${unit.abbreviation}`;
                                    }
                                    innerHtml += `<tr>
                                        <td style="${style}">&nbsp;</td>
                                        <td>${label} ${valueWithUnit}</td>
                                    </tr>`;
                                }
                            });

                        innerHtml += '</tbody>';

                        let tableRoot = tooltipEl.querySelector('table');
                        tableRoot.innerHTML = innerHtml;
                    }

                    // `this` will be the overall tooltip
                    let position = this._chart.canvas.getBoundingClientRect();

                    // Display, position, and set styles for font
                    tooltipEl.style.opacity = 1;
                    tooltipEl.style.position = 'absolute';
                    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                    tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                    tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
                    tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                    tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
                    tooltipEl.style.pointerEvents = 'none';
                }
            },
            scales: {
                yAxes: [{
                    scaleLabel: {
                        display: true,
                        labelString: yAxisLabelText,
                        fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: 14,
                        fontStyle: 100,
                        fontColor: "#999"
                    },
                    ticks: {
                        beginAtZero: true,
                        suggestedMax: maxYvalue * 1.1,
                        maxTicksLimit: 4,
                        fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: 18,
                        fontStyle: 100,
                        fontColor: "#010101",
                        callback: (value, index, values) => {
                            if (value === 0) {
                                return '0';
                            }
                            return this.formatYAxisNumber(value, 0);
                        }
                    }
                },{
                    //This second yaxis is a hack to chart the projects data at the topmost point of the chart.
                    id: 'project_yaxis',
                    position: 'right',
                    scaleLabel: {
                        display: false,
                        labelString: 'Number Of Projects included in Implemented Data',
                        fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: 12,
                        fontStyle: 400,
                        fontColor: "#010101"
                    },
                    ticks: {
                        display: false,
                        fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: 12,
                        fontStyle: 300,
                        fontColor: "#010101",
                        min: Number.MIN_SAFE_INTEGER, //unless the number of projects is close to MIN_SAVE_INT than 1 we'll display this at the top of the chart
                        max: 100,
                        maxTicksLimit: 2
                    },
                    gridLines: {
                        display: false,
                    }
                }],
                xAxes: [{
                    position: 'bottom',
                    scaleLabel: {
                        display: true,
                        labelString: '', // Apparently we don't label year. 'Year',
                        fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: 18,
                        fontStyle: 100,
                        fontColor: "#CCC"
                    },
                    ticks: {
                        fontFamily: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        fontSize: 18,
                        fontStyle: 100,
                        fontColor: "#010101",
                        maxRotation: 45,
                        minRotation: 45,
                        precision: 2,
                        stepSize: 2,
                        autoSkip: false,
                        callback: (value, index, values) => {
                            if ( (index % 6 === 0) || (values.length === index) ) {
                                return value;
                            }
                            if (index % 2 === 0) {
                                return ""
                            }
                            return null;
                        }
                    }
                }],
            },
            plugins: {
                datalabels: { //keep data labels on the points off by default. We only turn it on for the projects dataset.
                    display: false,
                    font: {
                        family: 'proxima-nova, "Helvetica Neue", Helvetica, Arial, sans-serif',
                        size: 16,
                        weight: 'bold'
                    }
                }
            },
            hover: {
                mode: 'index',
                intersect: false,
                onHover: this.hoverElements
            },
        };

        return <Row>
            <ConfirmationDialog
                Title={this.state.popup?.title}
                Body={this.state.popup?.content}
                ConfirmationButtonMessage="Close"
                Display={this.state.popup}
                OnConfirm={() => this.setState({popup: null})}
            />
            <Col xs={12} md={12}>
                <div className="sb-activity-feed-controls float-right">
                    <Row>
                        <Col xs={6}>
                            {this.state.environmentalBenefits?.length > 1 ?
                                <EnvironmentalBenefitFilter
                                    EnvironmentalBenefits={this.state.environmentalBenefits}
                                    Value={this.state.currentEnvironmentalBenefitId}
                                    UpdateValue={this.updateEnvironmentalBenefit}
                                    HideUnselectedOption={true}
                                />
                                : <br/>
                            }
                        </Col>
                        <Col xs={6}>
                            {this.state.conservationActionPhases?.length > 1 ?
                                <ConservationActionPhaseFilter
                                    ConservationActionPhases={this.state.conservationActionPhases}
                                    Value={this.state.currentConservationActionPhaseIndex}
                                    UpdateValue={this.updateConservationActionPhase}
                                    HideUnselectedOption={true}
                                />
                                : <br/>
                            }
                        </Col>
                    </Row>
                </div>
            </Col>
            <Conditional Condition={!envBenefit}>
                <Col xs={12}>
                    <br/>
                    <div className="sb-no-items-warning">
                        No Environmental Benefits have been defined for this program.
                        <HelpIcon>
                            Environmental Benefits can be designated on the Program Summary Edit page.
                        </HelpIcon>
                    </div>
                    <br/>
                </Col>
            </Conditional>
            <Conditional Condition={envBenefit && !this.state.data}>
                <Col xs={12}>
                    <br/>
                    <NoModeledUpliftWarning/>
                    <br/>
                </Col>
            </Conditional>
            <Col xs={12} md={12}>
                <Conditional Condition={envBenefit && this.state?.data}>
                    <h3 className="sb-chart-title">{this.getParentProgramShortTitle()} - {envBenefit?.name}</h3>
                    <Conditional Condition={this.state.data}>
                        <Row>
                            <Col xs={12} md={10} style={{"paddingRight":0}}>
                                <Line
                                    data={this.state.data}
                                    legend={legendConfig}
                                    options={chartOptions}
                                    ref={this.chartReference}
                                    getElementAtEvent={(element) => this.clickElement(element)}
                                />
                            </Col>
                            <Col xs={12} md={2} style={{"paddingLeft":0}}>
                                <StreamBankChartLegend Data={this.state.data}
                                                       OnClick={this.toggleSeries}
                                                       Series={this.state.series.sort(this.dataSeriesSort)}
                                                       EnvironmentalBenefit={envBenefit}
                                />
                            </Col>
                        </Row>
                    </Conditional>
                </Conditional>
            </Col>
        </Row>;
    }
}


